import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { listUserAvatars, uploadAvatar, deleteMediaAsset } from '@/services/media';
import { useAuth } from '@/auth/AuthContext';
import type { MediaAsset } from '@/domain/types';

interface Props {
  selected?: string;
  onSelect: (url: string) => void;
  onClose: () => void;
}

export function AvatarPicker({ selected, onSelect, onClose }: Props) {
  const { user } = useAuth();
  const [avatars, setAvatars] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(selected ?? '');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const userId = user?.id;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    listUserAvatars(userId)
      .then(setAvatars)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const handleUpload = async (file: File) => {
    if (!userId) return;
    if (!file.type.startsWith('image/')) {
      setUploadError('Please upload an image file.');
      return;
    }
    setUploadError('');
    setUploading(true);
    try {
      await uploadAvatar(userId, file);
      const updated = await listUserAvatars(userId);
      setAvatars(updated);
      if (updated[0]?.publicUrl) setPicked(updated[0].publicUrl);
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (asset: MediaAsset) => {
    if (!userId) return;
    try {
      await deleteMediaAsset(asset);
      setAvatars((prev) => prev.filter((a) => a.id !== asset.id));
      if (picked === asset.publicUrl) setPicked('');
    } catch {
      // silently fail
    }
  };

  const handleConfirm = () => {
    if (picked) { onSelect(picked); onClose(); }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    e.target.value = '';
  };

  const avatarCount = avatars.length;
  const atLimit = avatarCount >= 2;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden"
          style={{ maxHeight: '88vh' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-brand-50 flex items-center justify-center shrink-0">
                <i className="ri-user-smile-line text-brand-600" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-surface-900">Choose Avatar</h2>
                <p className="text-xs text-surface-400">
                  {avatars.length}/2 avatars · max 2
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500 transition-colors">
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 min-h-0">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <i className="ri-loader-4-line animate-spin text-3xl text-surface-300" />
                <p className="text-sm text-surface-400">Loading avatars…</p>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Avatar grid */}
                {avatarCount > 0 && (
                  <div>
                    <p className="text-xs font-medium text-surface-500 mb-3">
                      {avatarCount} of 2 avatars — click to select
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      {avatars.map((asset) => {
                        const isSelected = picked === asset.publicUrl;
                        return (
                          <div key={asset.id} className="relative">
                            <button
                              type="button"
                              onClick={() => asset.publicUrl && setPicked(asset.publicUrl)}
                              className={`w-full rounded-xl overflow-hidden border-2 transition-all duration-150 focus:outline-none ${
                                isSelected
                                  ? 'border-brand-500 shadow-lg shadow-brand-200/50 ring-2 ring-brand-200'
                                  : 'border-surface-200 hover:border-brand-300 hover:shadow-md'
                              }`}>
                              <div className="aspect-square bg-surface-100">
                                {asset.publicUrl && (
                                  <img
                                    src={asset.publicUrl}
                                    alt={asset.displayName}
                                    className="w-full h-full object-cover"
                                  />
                                )}
                              </div>
                              {isSelected && (
                                <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center shadow-sm">
                                  <i className="ri-check-line text-white text-sm font-bold" />
                                </div>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(asset)}
                              className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-md transition-colors"
                              title="Delete avatar">
                              <i className="ri-close-line text-white text-xs" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Drag & drop upload zone */}
                <div
                  onDragEnter={handleDragEnter}
                  onDragOver={(e) => e.preventDefault()}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  className={`rounded-2xl border-2 border-dashed transition-all cursor-pointer flex flex-col items-center justify-center gap-4 py-10 px-6 text-center ${
                    isDragging
                      ? 'border-brand-400 bg-brand-50'
                      : uploading
                      ? 'border-surface-200 bg-surface-50 cursor-wait'
                      : avatarCount === 0
                      ? 'border-brand-300 bg-brand-50/30 hover:bg-brand-50/60'
                      : 'border-surface-300 bg-surface-50/50 hover:border-brand-400 hover:bg-brand-50/30'
                  }`}>
                  {uploading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-3xl text-brand-500" />
                      <p className="text-sm font-semibold text-surface-700">Uploading avatar…</p>
                    </>
                  ) : isDragging ? (
                    <>
                      <div className="w-16 h-16 rounded-2xl bg-brand-100 flex items-center justify-center animate-bounce">
                        <i className="ri-upload-cloud-2-line text-3xl text-brand-600" />
                      </div>
                      <p className="text-base font-semibold text-brand-600">Drop your image here</p>
                    </>
                  ) : (
                    <>
                      <div className="w-16 h-16 rounded-2xl bg-white border border-surface-200 shadow-sm flex items-center justify-center">
                        <i className="ri-upload-cloud-2-line text-3xl text-surface-400" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-surface-700 mb-1">
                          {avatarCount === 0 ? 'Upload your first avatar' : atLimit ? 'Replace an avatar' : 'Add another avatar'}
                        </p>
                        <p className="text-xs text-surface-400 mb-3">
                          {atLimit
                            ? 'Uploading will replace the oldest avatar'
                            : 'Drag & drop or click to browse'}
                        </p>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                          className="px-5 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors shadow-sm">
                          <i className="ri-folder-open-line mr-2" />Browse Files
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {uploadError && (
                  <div className="flex items-center gap-2.5 p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                    <i className="ri-error-warning-line shrink-0" /> {uploadError}
                  </div>
                )}

                <p className="text-xs text-surface-400 text-center">
                  You can have up to 2 avatars. Uploading a 3rd will replace the oldest.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-surface-200 flex items-center justify-between shrink-0 bg-surface-50/50">
            <div className="flex items-center gap-3 min-w-0">
              {picked ? (
                <>
                  <img
                    src={picked}
                    alt="selected"
                    className="w-10 h-10 rounded-lg object-cover border border-surface-200 shrink-0"
                  />
                  <p className="text-xs font-semibold text-surface-700">Selected</p>
                </>
              ) : (
                <p className="text-xs text-surface-400">Click an avatar to select it</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-4">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-surface-300 hover:border-surface-400 text-surface-700 text-sm font-medium transition-colors">
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!picked}
                className="px-5 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors">
                Use This Avatar
              </button>
            </div>
          </div>
        </div>
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileInput} className="hidden" />
    </>,
    document.body,
  );
}
